import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Self-contained on purpose: index.js already extends these plugins on app
// boot, but this module shouldn't rely on that happening first (Jest tests,
// Storybook, or any future entry point that imports this file directly would
// otherwise hit "dayjs(...).tz is not a function"). dayjs.extend() is
// idempotent, so re-extending here is safe even though it's redundant in the
// normal app boot path.
dayjs.extend(utc);
dayjs.extend(timezone);

export const getTenantTimezone = () =>
  localStorage.getItem("tenantTimezone") || "Asia/Kolkata";

export const formatInTenantTz = (isoString, fmt = "MMM DD, YYYY, hh:mm A") => {
  if (!isoString) return "—";
  return dayjs(isoString).tz(getTenantTimezone()).format(fmt);
};

export const formatDateOnly = (isoString) => {
  if (!isoString) return "—";
  // Date-only values (DOB, invoice/due dates, subscription start/end, etc.)
  // have no time-of-day component, so there's no instant to convert between
  // timezones. Parsing as tenant-local and then calling .tz() would instead
  // reinterpret midnight in the *browser's* zone, which can shift the
  // calendar day by ±1 depending on where the browser happens to be — parse
  // in UTC mode and never convert, so the calendar date is never touched.
  return dayjs.utc(isoString).format("DD/MM/YYYY");
};

// dayjs has no "abbreviated timezone name" token — "z" prints the literal
// letter z, and .offsetName('short') falls back to Intl, which doesn't know
// national abbreviations for most of our tenants' zones (Asia/Kolkata comes
// back as "GMT+5:30", not "IST"). Hardcode the fixed-offset zones we actually
// see across tenants; for anything else (e.g. US/EU zones with DST) fall
// back to Intl, which *does* correctly resolve those to EST/EDT, GMT/BST etc.
const FIXED_OFFSET_TZ_ABBREVIATIONS = {
  "Asia/Kolkata": "IST",
  "Asia/Calcutta": "IST",
  "Asia/Colombo": "IST",
  "Asia/Dubai": "GST",
  "Asia/Muscat": "GST",
  "Asia/Riyadh": "AST",
  "Asia/Qatar": "AST",
  "Asia/Kuwait": "AST",
  "Asia/Bahrain": "AST",
  "Asia/Karachi": "PKT",
  "Asia/Dhaka": "BST",
  "Asia/Kathmandu": "NPT",
  "Asia/Singapore": "SGT",
  "Asia/Hong_Kong": "HKT",
  "Asia/Shanghai": "CST",
  "Asia/Tokyo": "JST",
  "Asia/Seoul": "KST",
  "Asia/Bangkok": "ICT",
  "Asia/Jakarta": "WIB",
  "Asia/Manila": "PHT",
  UTC: "UTC",
  "Etc/UTC": "UTC",
};

const getTzAbbreviation = (tz, date) => {
  if (FIXED_OFFSET_TZ_ABBREVIATIONS[tz]) return FIXED_OFFSET_TZ_ABBREVIATIONS[tz];
  try {
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName");
    return part ? part.value : tz;
  } catch (e) {
    return tz;
  }
};

// The single place to update if the app-wide "date + time + zone" display
// format ever needs to change — every table/list rendering a full
// timestamp should call this instead of formatting inline.
export const formatWithTzLabel = (isoString, fmt = "DD MMM YYYY, hh:mm A") => {
  if (!isoString) return "—";
  const tz = getTenantTimezone();
  const d = dayjs(isoString).tz(tz);
  return `${d.format(fmt)} ${getTzAbbreviation(tz, d.toDate())}`;
  // e.g. "27 Aug 2026, 06:24 PM IST"  or  "27 Aug 2026, 06:24 PM GST"
};
